import { makeAutoObservable, reaction } from "mobx";
import { computedFn } from "mobx-utils";

import {
  createChat,
  deleteChat,
  getApiErrorMessage,
  getChat,
  listTeamChats,
  listTeamDeletedChats,
  restoreChat,
} from "@/api";
import { onCableMessage } from "@/api/cable";
import { ChatModel } from "@/models/chat/ChatModel";
import { ObservedBoolMap } from "@/utils/ObservedBoolMap";

import type { Chat, ChatShort } from "@/api";
import type { ChatsStatus, CreatingChatStatus } from "@/store/ChatsStore";
import type { RootStore } from "@/store/RootStore";

class ChatMap extends ObservedBoolMap<[chat: ChatModel]> {
  protected mapKey(chat: ChatModel) {
    return chat.uid;
  }
}

export class TeamChatsStore {
  private rootStore: RootStore;

  private chatsMap = new Map<string, ChatModel>();

  private fetchChatsPromise?: Promise<void>;

  private fetchAbortController?: AbortController;

  private trashAbortController?: AbortController;

  private currentTeamReactionDispose?: () => void;

  private cableUnsubscribe?: () => void;

  /** Deleted chat uid → the channel that sat next to it in the list, if any. */
  private deletedChatNeighbors = new Map<string, string | undefined>();

  editChatMap = new ChatMap();

  isSectionOpen = true;

  isListExpanded = false;

  status: ChatsStatus = "idle";

  creatingChatStatus: CreatingChatStatus = "idle";

  error = "";

  trashedChats: ChatShort[] = [];

  trashStatus: ChatsStatus = "idle";

  isTrashOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);

    this.currentTeamReactionDispose = reaction(
      () => this.team?.uid,
      (_teamUid, previousTeamUid) => {
        // The team first resolving (teams just loaded) isn't a switch: nothing
        // team-scoped has been fetched yet, but a chat fetched by uid may have.
        if (previousTeamUid === undefined) return;
        this.fetchAbortController?.abort();
        this.fetchAbortController = undefined;
        this.disposeChats();
        this.deletedChatNeighbors.clear();
        this.editChatMap.clear();
        this.fetchChatsPromise = undefined;
        this.isListExpanded = false;
        this.isSectionOpen = true;
        this.setStatus("idle");
        this.resetTrash();
      },
    );

    this.cableUnsubscribe = onCableMessage((payload) => {
      if (payload.type !== "chat_deleted" && payload.type !== "chat_restored") return;
      const isCurrentTeam = payload.team_id === this.team?.uid;

      if (payload.type === "chat_deleted") {
        // The mobile WebView fetches its chat by uid without switching the current team
        if (isCurrentTeam || this.chatsMap.has(payload.chat_id)) this.removeChat(payload.chat_id);
        if (isCurrentTeam && this.trashStatus !== "idle") void this.fetchTrashedChats();
      } else if (isCurrentTeam || this.isChatDeleted(payload.chat_id)) {
        this.addRestoredChat(payload.chat_id);
      }
    });
  }

  private resetTrash() {
    this.trashAbortController?.abort();
    this.trashAbortController = undefined;
    this.trashedChats = [];
    this.trashStatus = "idle";
    this.isTrashOpen = false;
  }

  private addRestoredChat(uid: string) {
    this.deletedChatNeighbors.delete(uid);
    this.trashedChats = this.trashedChats.filter((trashed) => trashed.uid !== uid);
    if (this.chatsMap.has(uid)) return;

    const teamUid = this.team?.uid;
    getChat(uid)
      .then(({ chat }) => {
        if (chat && this.team?.uid === teamUid) this.updateChats([chat]);
      })
      .catch((err) => {
        console.error("Failed to fetch restored chat:", err);
      });
  }

  private disposeChats() {
    for (const [uid, chat] of this.chatsMap) {
      chat.dispose();
      this.chatsMap.delete(uid);
    }
  }

  get team() {
    return this.rootStore.teams.currentTeam;
  }

  get isAvailable() {
    return !!this.team && !this.team.personal;
  }

  get collectiveChats() {
    return [...this.chatsMap.values()]
      .filter((chat) => !chat.withBot)
      .sort((a, b) => b.lastActivityCursor.localeCompare(a.lastActivityCursor));
  }

  get collectiveChatsCount() {
    return this.collectiveChats.length;
  }

  get unreadChats() {
    return this.collectiveChats.filter((chat) => chat.unreadMessagesCount > 0);
  }

  get unreadMessagesCount() {
    return this.unreadChats.reduce((sum, chat) => sum + chat.unreadMessagesCount, 0);
  }

  get hasUnreadChats() {
    return this.unreadChats.length > 0;
  }

  // Unlike an inline findIndex over collectiveChats, this keeps observers
  // untouched while messages reorder nothing (the returned index is
  // value-diffed, the array reference is not)
  collectiveChatIndex = computedFn((uid: string | undefined): number => {
    if (!uid) return -1;
    return this.collectiveChats.findIndex((chat) => chat.uid === uid);
  });

  getChat(uid: string) {
    return this.chatsMap.get(uid);
  }

  /** Whether the chat was deleted (here or by another member) since the app loaded. */
  isChatDeleted(uid: string) {
    return this.deletedChatNeighbors.has(uid);
  }

  /** The channel to open in place of a deleted one: the one above it, else the one below. */
  chatReplacingDeleted(uid: string): ChatModel | undefined {
    const neighborUid = this.deletedChatNeighbors.get(uid);
    return neighborUid ? this.chatsMap.get(neighborUid) : undefined;
  }

  removeChat(uid: string) {
    if (!this.deletedChatNeighbors.has(uid)) {
      const chats = this.collectiveChats;
      const index = chats.findIndex((chat) => chat.uid === uid);
      const neighbor = index === -1 ? undefined : (chats[index - 1] ?? chats[index + 1]);
      this.deletedChatNeighbors.set(uid, neighbor?.uid);
    }
    if (this.editChatMap.current === uid) this.editChatMap.reset();
    this.chatsMap.get(uid)?.dispose();
    this.chatsMap.delete(uid);
  }

  async deleteChat(chat: ChatModel) {
    await deleteChat(chat.uid);
    this.removeChat(chat.uid);
  }

  async restoreChat(uid: string) {
    const { chat } = await restoreChat(uid);
    if (chat) this.updateChats([chat]);
    this.addRestoredChat(uid);
  }

  toggleTrash() {
    this.isTrashOpen = !this.isTrashOpen;
    if (this.isTrashOpen) void this.fetchTrashedChats();
  }

  /** Fetch the team's deleted channels; a newer call supersedes one still in flight. */
  async fetchTrashedChats(): Promise<void> {
    const { team } = this;
    if (!team) return;

    this.trashAbortController?.abort();
    const abortController = new AbortController();
    this.trashAbortController = abortController;

    try {
      this.setTrashStatus("loading");
      const { chats } = await listTeamDeletedChats(team.permalink, {
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;

      this.setTrashedChats(chats ?? []);
      this.setTrashStatus("loaded");
    } catch {
      if (abortController.signal.aborted) return;
      this.setTrashStatus("error");
    }
  }

  private setTrashedChats(chats: ChatShort[]) {
    this.trashedChats = chats;
  }

  private setTrashStatus(status: ChatsStatus) {
    this.trashStatus = status;
  }

  /**
   * Fetch one chat by uid and add it to the store, subscribed for realtime like
   * a listed chat. Used by the mobile team-chat WebView, which is addressed by
   * chat uid and never loads the team list. Resolves to the model, or undefined
   * when the server has no such chat.
   */
  async fetchChat(
    uid: string,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<ChatModel | undefined> {
    try {
      this.setStatus("loading");
      this.setError("");
      const { chat } = await getChat(uid, undefined, { signal });
      if (signal?.aborted) return undefined;

      if (chat) this.updateChats([chat]);
      this.setStatus("loaded");
      return this.chatsMap.get(uid);
    } catch (err) {
      if (signal?.aborted) return undefined;
      this.setError(getApiErrorMessage(err));
      this.setStatus("error");
      return undefined;
    }
  }

  toggleSection() {
    this.isSectionOpen = !this.isSectionOpen;
  }

  expandList() {
    this.isListExpanded = true;
  }

  setStatus(status: ChatsStatus) {
    this.status = status;
  }

  setCreatingChatStatus(status: CreatingChatStatus) {
    this.creatingChatStatus = status;
  }

  setError(error: string) {
    this.error = error;
  }

  fetchChats(): Promise<void> {
    const { team } = this;
    if (!team) return Promise.resolve();

    if (this.fetchChatsPromise) return this.fetchChatsPromise;

    const abortController = new AbortController();
    this.fetchAbortController = abortController;
    const deletedBeforeFetch = new Set(this.deletedChatNeighbors.keys());

    const promise = (async () => {
      try {
        this.setStatus("loading");
        this.setError("");
        const result = await listTeamChats(team.permalink, { signal: abortController.signal });
        if (abortController.signal.aborted) return;

        if (result.chats) {
          // A chat deleted while this request was in flight is stale in the response
          this.updateChats(
            result.chats.filter(
              (chat) =>
                deletedBeforeFetch.has(chat.uid) || !this.deletedChatNeighbors.has(chat.uid),
            ),
          );
        }
        this.setStatus("loaded");
      } catch (err) {
        this.setError(getApiErrorMessage(err));
        this.setStatus("error");
      }
    })();

    this.fetchChatsPromise = promise;
    promise.finally(() => {
      if (this.fetchChatsPromise === promise) {
        this.fetchChatsPromise = undefined;
      }
    });
    return promise;
  }

  async createChat({ signal }: { signal?: AbortSignal } = {}): Promise<ChatModel | undefined> {
    const { team } = this;
    if (!team) return undefined;

    try {
      this.setCreatingChatStatus("creating");
      const { chat } = await createChat({ teamUid: team.uid, kind: "collective" }, { signal });
      if (signal?.aborted || !chat) return undefined;

      this.updateChats([chat]);
      this.setCreatingChatStatus("created");
      return this.chatsMap.get(chat.uid);
    } catch {
      this.setCreatingChatStatus("error");
      return undefined;
    }
  }

  private updateChats(chats: Chat[]) {
    chats.forEach((c) => {
      this.deletedChatNeighbors.delete(c.uid);
      if (!this.chatsMap.has(c.uid)) {
        const model = new ChatModel(c, "", this.rootStore.auth.currentUser);
        this.chatsMap.set(c.uid, model);
        model.subscribe();
      }
    });
  }

  dispose() {
    this.currentTeamReactionDispose?.();
    this.cableUnsubscribe?.();
    this.fetchAbortController?.abort();
    this.trashAbortController?.abort();
    this.disposeChats();
  }
}
